export interface SiteNavLink {
  name: string;
  path: string;
  external?: boolean;
}

export interface SiteStat {
  label: string;
  value: string;
}

export interface SiteListItem {
  title: string;
  description: string;
}

export interface SiteSocialLink {
  name: string;
  href: string;
}

export interface SiteContactContent {
  email: string;
  phonePrimary: string;
  phonePrimaryHref?: string;
  phoneSecondary: string;
  phoneSecondaryHref?: string;
  whatsapp: string;
  whatsappHref?: string;
  mtnMoney: string;
  airtelMoneyDirect: string;
  airtelMerchantCode: string;
  addressLine1: string;
  cityCountry: string;
  officeHours: string[];
  websiteLabel: string;
}

export interface SiteSettings {
  navLinks: SiteNavLink[];
  contact: SiteContactContent;
  socials: SiteSocialLink[];
  intake: {
    label: string;
    message: string;
    status: string;
  };
  stats: SiteStat[];
  homePrograms: string[];
  aboutValues: SiteListItem[];
  classFormats: SiteListItem[];
}

export interface PricingTierContent {
  id: string;
  name: string;
  price: {
    ugx: number;
    label: string;
  };
  duration: string;
  popular?: boolean;
  description: string;
  ageRange: string;
  features: string[];
  benefits: string[];
  courseContent: string[];
  learningOutcomes: string[];
  professionalSkills?: string[];
  careerPreparation?: string[];
}

export interface PricingFaq {
  question: string;
  answer: string;
}

export interface PricingContent {
  tiers: PricingTierContent[];
  whoItsFor: SiteListItem[];
  proofStats: SiteStat[];
  proofMessage: string;
  discounts: {
    familyOfferTitle: string;
    familyOfferDescription: string;
    partnershipTitle: string;
    partnershipDescription: string;
    partnershipExample: string;
  };
  faqs: PricingFaq[];
}

export interface AcceleratorContent {
  hero: {
    eyebrow: string;
    headline: string;
    description: string;
    primaryCta: string;
    secondaryCta: string;
  };
  offer: {
    duration: string;
    priceUgx: number;
    priceUsd: number;
    priceNote: string;
    cardDescription: string;
  };
  introCards: SiteListItem[];
  bestFor: {
    eyebrow: string;
    title: string;
    items: string[];
  };
  whatStaysSame: {
    eyebrow: string;
    title: string;
    items: string[];
  };
  howItWorks: {
    eyebrow: string;
    title: string;
    steps: SiteListItem[];
  };
  guidance: {
    eyebrow: string;
    title: string;
    description: string;
    compareLabel: string;
  };
}

export const defaultSiteSettings: SiteSettings = {
  navLinks: [
    { name: 'Home', path: '/' },
    { name: 'Explore', path: '/explore' },
    { name: 'GiIT LMS', path: 'https://lms.giitlearn.com/', external: true },
    { name: 'About', path: '/about' },
    { name: 'Programs', path: '/services' },
    { name: 'Pricing', path: '/pricing' },
    { name: 'Gallery', path: '/gallery' },
    { name: 'Contact', path: '/contact' },
  ],
  contact: {
    email: 'info@giitlearn.com',
    phonePrimary: '+256 791 418 501',
    phoneSecondary: '+256 761 440 460',
    whatsapp: '+256 791 418 501',
    mtnMoney: '+256791418501',
    airtelMoneyDirect: '+256761440460',
    airtelMerchantCode: '6708507',
    addressLine1: 'National ICT Innovation Hub',
    cityCountry: 'Uganda',
    officeHours: [
      'Monday - Friday: 8:00 AM - 6:00 PM',
      'Saturday: 9:00 AM - 4:00 PM',
      'Sunday: Closed',
    ],
    websiteLabel: 'www.geniusinstituteofit.org',
  },
  socials: [
    { name: 'Facebook', href: 'https://www.facebook.com/' },
    { name: 'Instagram', href: 'https://www.instagram.com/' },
    { name: 'LinkedIn', href: 'https://www.linkedin.com/' },
  ],
  intake: {
    label: 'April 2026 Intake',
    message: 'Seats are filling fast. Join 600+ students already transforming their futures.',
    status: 'April 2026 intake open now',
  },
  stats: [
    { label: 'Students', value: '600+' },
    { label: 'Programs', value: '12' },
    { label: 'Years', value: '4+' },
  ],
  homePrograms: [
    'Full-Stack Web Dev',
    'AI & Machine Learning',
    'Robotics & IoT',
    'Digital Skills',
  ],
  aboutValues: [
    {
      title: 'Student-Centered',
      description: 'We design every learning path around learner progress, confidence, and support.',
    },
    {
      title: 'Excellence',
      description: 'We aim for work students can be proud to present, not just tasks they complete.',
    },
    {
      title: 'Innovation',
      description: 'We teach modern tools and creative thinking that connect learning to real-world use.',
    },
    {
      title: 'Continuous Growth',
      description: 'We help learners move from foundations to advanced skills through steady progression.',
    },
  ],
  classFormats: [
    {
      title: 'Remote Classes',
      description: 'Interactive live learning from anywhere with structure, practice, and mentor support.',
    },
    {
      title: 'One-on-One',
      description: 'A personalized path for learners who need focused support and flexible pacing.',
    },
    {
      title: 'Physical Classes',
      description: 'Hands-on classroom learning with collaboration, coaching, and guided problem-solving.',
    },
    {
      title: 'Group Classes',
      description: 'Community-driven learning where students build skills alongside motivated peers.',
    },
  ],
};

export const defaultPricingContent: PricingContent = {
  tiers: [
    {
      id: 'monthly-subscription',
      name: 'Monthly Subscription',
      price: { ugx: 400000, label: 'UGX 400,000/month' },
      duration: 'Monthly',
      popular: true,
      description: 'Continuous learning access for families who want flexibility month to month.',
      ageRange: 'All ages',
      features: [
        'Access to all available courses',
        'Unlimited learning for all children in one household',
        'No long-term commitment',
        'Cancel anytime',
        'Best value for families with multiple children',
      ],
      benefits: [
        'One monthly plan for the whole family',
        'Easy exploration across multiple technologies',
        'Continuous practice without gaps',
        'Flexible for changing schedules',
      ],
      courseContent: [
        'All available courses and modules',
        'Live instructor sessions',
        'Project-based learning',
        'Peer collaboration opportunities',
      ],
      learningOutcomes: [
        'Continuous skill development',
        'Technology literacy across multiple domains',
        'Confidence in digital tools and programming',
        'Strong foundation for advanced learning',
      ],
    },
    {
      id: 'mini-courses',
      name: 'Mini Courses',
      price: { ugx: 400000, label: 'UGX 400,000' },
      duration: '2 months',
      description: 'A focused foundation for beginners who want to test interest and build confidence.',
      ageRange: 'Ages 5-18',
      features: [
        'Scratch programming fundamentals',
        'Basic computer literacy',
        'Introduction to problem-solving',
        'Creative technology projects',
        'Portfolio of 3-5 completed projects',
      ],
      benefits: [
        'Strong foundation in computational thinking',
        'Confidence using technology creatively',
        'Gentle introduction to programming concepts',
        'Age-appropriate learning support',
      ],
      courseContent: [
        'Visual programming for logical thinking',
        'Essential digital skills and safety',
        'Critical thinking through puzzles and games',
        'Creativity-led technology projects',
      ],
      learningOutcomes: [
        'Basic programming concept mastery',
        'Creative project completion',
        'Digital literacy skills',
        'Problem-solving abilities',
      ],
    },
    {
      id: 'comprehensive-courses',
      name: 'Comprehensive Courses',
      price: { ugx: 1500000, label: 'UGX 1,500,000' },
      duration: '3 months',
      description: 'A deeper track for learners ready to build serious technical skills and bigger projects.',
      ageRange: 'Ages 9-18',
      features: [
        'Python programming',
        'Web development',
        'Database fundamentals',
        'Introduction to AI',
        'Project management and version control',
        'Mobile development basics',
      ],
      benefits: [
        'Multi-skill technical growth',
        'Ability to create functional web applications',
        'Understanding of software development workflows',
        'Portfolio of 8-12 substantial projects',
      ],
      courseContent: [
        'Programming language fundamentals',
        'Interactive website creation',
        'Data storage and retrieval concepts',
        'Practical AI foundations',
        'Professional development practices',
        'Mobile app basics',
      ],
      learningOutcomes: [
        'Multi-language programming proficiency',
        'Web application development skills',
        'Professional development practices',
        'Technology career preparation',
      ],
      professionalSkills: [
        'Problem-solving methodologies',
        'Collaborative development through team projects',
        'Technical communication through presentations',
        'Critical thinking for complex systems',
      ],
    },
    {
      id: 'full-courses',
      name: 'Full Courses',
      price: { ugx: 3500000, label: 'UGX 3,500,000' },
      duration: '6 months',
      description: 'Advanced preparation for students aiming at serious portfolios, internships, and future CS pathways.',
      ageRange: 'Ages 13-18',
      features: [
        'Advanced Python and data science',
        'Full-stack web development',
        'Mobile app development',
        'AI and machine learning',
        'Robotics and IoT',
        'Cybersecurity fundamentals',
        'Software engineering practices',
        'Technology entrepreneurship',
      ],
      benefits: [
        'Professional-level programming skills',
        'Industry-ready portfolio with 15-20 projects',
        'Preparation for internships and advanced study',
        'Foundation for technology entrepreneurship',
      ],
      courseContent: [
        'Machine learning and automation',
        'Professional web application creation',
        'Android and iOS app development',
        'Practical AI application development',
        'Hardware programming and automation',
        'Professional engineering workflows',
      ],
      learningOutcomes: [
        'Professional-level multi-platform programming',
        'Industry-ready project portfolio',
        'Competitive programming readiness',
        'University admission preparation',
        'Technology entrepreneurship foundation',
      ],
      careerPreparation: [
        'University application portfolio development',
        'Professional networking through mentorship',
        'Internship placement assistance',
        'International coding olympiad preparation',
      ],
    },
  ],
  whoItsFor: [
    {
      title: 'Mini Courses',
      description: 'Best for first-time learners who need a manageable entry point and quick confidence.',
    },
    {
      title: 'Comprehensive Courses',
      description: 'Best for learners ready to build deeper technical fluency and more substantial projects.',
    },
    {
      title: 'Full Courses',
      description: 'Best for serious long-term preparation, advanced portfolios, and future academic or career pathways.',
    },
    {
      title: 'Monthly Subscription',
      description: 'Best for families who want broad access, flexibility, and the strongest value across multiple children.',
    },
  ],
  proofStats: [
    { label: 'Students trained', value: '600+' },
    { label: 'Learner success rate', value: '95%' },
  ],
  proofMessage: 'Students get guided practice, mentor feedback, and project-based progress instead of passive learning.',
  discounts: {
    familyOfferTitle: 'Monthly Family Offer',
    familyOfferDescription: 'One monthly subscription covers unlimited children in the same household, making it the clearest option for families with multiple tech-interested learners.',
    partnershipTitle: 'Long-Term Partnership Discount',
    partnershipDescription: 'Families with 3 or more children can discuss long-term planning at UGX 250,000 per child per course.',
    partnershipExample: 'Example: 3 children = UGX 750,000 total per course cycle.',
  },
  faqs: [
    {
      question: 'How do I choose the right plan?',
      answer: 'Start with the learner’s readiness and time commitment. Mini is best for beginners, Comprehensive suits growing confidence, Full Courses are for committed long-term growth, and Monthly is best for families who want broad access.',
    },
    {
      question: 'Can I pay in installments?',
      answer: 'Yes. Contact our team and we can guide you through the payment option that best fits your family’s schedule.',
    },
    {
      question: 'What if I have more than one child enrolling?',
      answer: 'The monthly subscription is the clearest family-friendly option because one monthly price covers unlimited children in the same household.',
    },
    {
      question: 'How do I confirm payment?',
      answer: 'After paying by mobile money, send confirmation by WhatsApp or call our enrollment team and we will complete the process with you.',
    },
  ],
};

export const defaultAcceleratorContent: AcceleratorContent = {
  hero: {
    eyebrow: 'Accelerator Program',
    headline: 'Shorter time, sharper focus, same serious learning intent.',
    description: 'The Accelerator pathway condenses any GiiT program into a focused two-week experience for learners who want fast momentum, structured support, and a clear next step without committing to the full-length schedule first.',
    primaryCta: 'Start with an Accelerator',
    secondaryCta: 'Compare Full Programs',
  },
  offer: {
    duration: '2 weeks',
    priceUgx: 150000,
    priceUsd: 41,
    priceNote: 'Two-week intensive delivery with a clear path into the full program',
    cardDescription: 'Build momentum in {focus} through a focused sprint designed to surface the essentials quickly and clearly.',
  },
  introCards: [
    {
      title: 'Fast decision',
      description: 'Use the accelerator when you need a structured yes-or-no decision quickly.',
    },
    {
      title: 'Real delivery',
      description: 'This is still guided learning with practical work, not a sampler pack.',
    },
    {
      title: 'Clear next step',
      description: 'Learners leave with enough clarity to continue into the full pathway confidently.',
    },
  ],
  bestFor: {
    eyebrow: 'Best for',
    title: 'Learners who need speed without losing structure.',
    items: [
      'Students who want quick momentum before school schedules shift or before joining a longer program.',
      'Learners who already know the area they want to explore and prefer a concentrated, guided sprint.',
      'Families who want a lower-commitment way to test fit, teaching style, and learner readiness.',
    ],
  },
  whatStaysSame: {
    eyebrow: 'What stays the same',
    title: 'Fast does not mean watered down.',
    items: [
      'The program still prioritizes practical work, mentor guidance, and confidence-building through active learning.',
      'Learners still get structured support and a focused pathway into the subject, not disconnected lessons.',
      'You trade breadth for speed. The accelerator is built to create clarity and momentum, then lead naturally into the full program if needed.',
    ],
  },
  howItWorks: {
    eyebrow: 'How it works',
    title: 'A simple three-step path.',
    steps: [
      {
        title: '1. Pick the subject',
        description: 'Choose the area where the learner needs speed, clarity, or a fast start.',
      },
      {
        title: '2. Learn intensively',
        description: 'Work through the essentials quickly with mentor support and focused practice.',
      },
      {
        title: '3. Decide the next move',
        description: 'Continue into the full program or use the accelerator as a sharp confidence-building sprint.',
      },
    ],
  },
  guidance: {
    eyebrow: 'Need guidance?',
    title: 'Talk to us before choosing.',
    description: 'If you are unsure whether the accelerator or the full program is the better fit, our team can help you compare the options quickly.',
    compareLabel: 'Compare full programs',
  },
};
